import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import {
  getSingleAddress,
  sendSingleDelivery,
  fetchSingleAddress,
} from './constants/mailerConstants';
import Icon from '../common/Icon';
import LinkedMailerInfo from './LinkedMailerInfo';
import { sortByAlpha } from '../../helpers/sorters';

const SingleMailerModal = (props) => {
  const {
    fields,
    mailers,
    responses,
    viewMailSend,
    closeModal,
    sendingFromGlobal,
    updateDeliveries,
    submissionId,
  } = props;
  const sortedMailers = sortByAlpha(mailers, 'name');
  const [selectedMailer, setSelectedMailer] = useState(null);
  const [bcc, setBcc] = useState(false);
  const [bccAddress, setBccAddress] = useState('');
  const [sendToAlternative, setSendToAlternative] = useState(false);
  const [alternativeEmail, setAlternativeEmail] = useState('');
  const [disableSend, setDisableSend] = useState(true);
  const [mailTo, setMailTo] = useState('');
  const [mailCount, setMailCount] = useState({ send: null, fail: null });
  const [isFetching, setIsFetching] = useState(false);
  const [groupData, setGroupData] = useState(null);
  // TODO: make error text appear in modal, along with dropdown...
  const [errorText, setErrorText] = useState('');

  const validateEmailAddress = (emailString) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emails = emailString.split(',').map((email) => email.trim());

    for (const email of emails) {
      if (email && !emailRegex.test(email)) {
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    if (sendingFromGlobal) {
      setIsFetching(false);
    }
  }, [mailCount]);

  useEffect(() => {
    let input = document.getElementById('alt-input');
    if (sendToAlternative && input) input.focus();
  }, [sendToAlternative]);

  useEffect(() => {
    let input = document.getElementById('bcc-input');
    if (bcc && input) input.focus();
  }, [bcc]);

  const handleMailerClick = async (e) => {
    if (selectedMailer?.id == e.target.id) return;
    let mailer = mailers.find((m) => m.id == e.target.id);
    setSelectedMailer(mailer);
    setErrorText('');
    let noMail = true;
    let address = '';

    // Process of sending from the Global Search page is slightly different
    if (sendingFromGlobal) {
      setIsFetching(true);
      let deliverable = await fetchSingleAddress(mailer.id, submissionId);
      if (deliverable.ok && deliverable.data.delivery) {
        const { determine_to_address } = deliverable.data.delivery;
        address = determine_to_address;
        setGroupData(deliverable.data.group);
      } else {
        setGroupData(null);
      }
    } else {
      // Process for sending directly from the Submission Show page
      let toField = fields.find((f) => f.id == mailer.field_id);
      let response = responses.find((r) => r.field_id == toField.id);
      address = getSingleAddress(mailer, response, toField);
    }

    setMailTo(address);
    if (!validateEmailAddress(address)) {
      setMailCount({ send: 0, fail: 1 });
    } else {
      noMail = false;
      setMailCount({ send: 1, fail: 0 });
    }
    noMail ? setDisableSend(true) : setDisableSend(false);
  };

  const handleModalClose = () => {
    setSelectedMailer(null);
    setBcc(false);
    setBccAddress('');
    setSendToAlternative(false);
    setAlternativeEmail('');
    setDisableSend(true);
    setMailTo('');
    setMailCount({ send: null, fail: null });
    setGroupData(null);
    closeModal();
  };

  return (
    <Modal
      show={viewMailSend}
      onClose={handleModalClose}
      title='send-mailer-modal'
    >
      <header>
        <h3>
          {sendingFromGlobal ? (
            <>Send Individual Email - Submission: {submissionId}</>
          ) : (
            'Send Individual Email'
          )}
        </h3>
      </header>

      <div className='send-mailer-content'>
        <div className='mailer-list-wrapper'>
          <h4>Select an email below to send:</h4>
          <ol className='mailer-list'>
            {mailers.length ? (
              sortedMailers.map((mailer) => {
                return (
                  <li
                    key={mailer.id}
                    className={
                      selectedMailer && mailer.id == selectedMailer.id
                        ? `mailer-${mailer.id} selected`
                        : `mailer-${mailer.id}`
                    }
                    id={mailer.id}
                    onClick={handleMailerClick}
                  >
                    {mailer.detected_forms?.length && (
                      <>
                        <Icon
                          icon='link'
                          title='Mailer utilizes related forms'
                        />
                        &nbsp;
                      </>
                    )}
                    {mailer.name}
                  </li>
                );
              })
            ) : (
              <li className='empty'>Create a mailer first!</li>
            )}
          </ol>
        </div>

        {selectedMailer && (
          <div className='mail-send flex'>
            <div className='mail-count'>
              <h3>Emails to Send:</h3>
              {sendToAlternative ? (
                <p>{validateEmailAddress(alternativeEmail) ? '1' : '0'}</p>
              ) : !isFetching ? (
                <p>{mailCount.send}</p>
              ) : (
                <></>
              )}
            </div>

            <div className='mail-info'>
              <h3>Sending To:</h3>
              {!isFetching && (
                <p className={'mail-to' + (disableSend ? ' error' : '')}>
                  {sendToAlternative
                    ? alternativeEmail || 'Provide alternative email...'
                    : mailTo}
                </p>
              )}
            </div>
            <br />
            <div className='mail-option'>
              <input
                type='checkbox'
                id='alternative-email'
                checked={sendToAlternative}
                onChange={() => {
                  if (sendToAlternative) {
                    setAlternativeEmail('');
                  }
                  setSendToAlternative(!sendToAlternative);
                }}
              />
              <label htmlFor='alternative-email'>Alternative Email</label>
              <input
                id='alt-input'
                type='text'
                placeholder='Alternative address'
                value={alternativeEmail}
                style={{ display: !sendToAlternative ? 'none' : '' }}
                onChange={(e) => setAlternativeEmail(e.target.value)}
              />
            </div>

            <div className='mail-option'>
              <input
                type='checkbox'
                id='bcc-checkbox'
                checked={bcc}
                onChange={() => {
                  if (bcc) {
                    setBccAddress('');
                  }
                  setBcc(!bcc);
                }}
              />
              <label htmlFor='bcc-checkbox'>Include BCC</label>
              <input
                id='bcc-input'
                type='text'
                placeholder='BCC address'
                value={bccAddress}
                style={{ display: !bcc ? 'none' : '' }}
                onChange={(e) => setBccAddress(e.target.value)}
              />
            </div>

            {isFetching ? (
              <button className='batch-mail-btn' disabled>
                Send Email
              </button>
            ) : (
              <button
                className='single-mail-btn'
                disabled={
                  sendToAlternative
                    ? !validateEmailAddress(alternativeEmail)
                    : disableSend
                }
                onClick={(e) => {
                  e.target.blur();
                  sendSingleDelivery({
                    mailer: selectedMailer,
                    subId: submissionId,
                    mailTo: mailTo,
                    bccAddress: bccAddress,
                    updateDeliveries: updateDeliveries,
                    alternativeEmail: alternativeEmail,
                    closeModal: handleModalClose,
                  });
                }}
              >
                Send Email
              </button>
            )}
          </div>
        )}
      </div>
      {selectedMailer?.detected_forms?.length > 0 && !isFetching && (
        <LinkedMailerInfo
          batch={false}
          detectedForms={selectedMailer.detected_forms}
          subId={submissionId}
          groupData={groupData}
        />
      )}
    </Modal>
  );
};

export default SingleMailerModal;
